<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import api from '@/services/api';
import { useUserStore } from '@/stores/useUserStore';

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

const isLoading = ref(true);
const result = ref({
  success: false,
  message: 'Processando...'
});

onMounted(async () => {
  console.log("🔍 Query params recebidos:", route.query);

  // O Mercado Pago pode enviar vários parâmetros na URL de retorno:
  // - preapproval_id (ou subscription_id)
  // - status
  // - external_reference
  const preapprovalId = route.query.preapproval_id || route.query.subscription_id;
  const statusFromUrl = route.query.status;
  const externalReference = route.query.external_reference;

  console.log("📋 Dados extraídos:", { preapprovalId, statusFromUrl, externalReference });

  // Aguarda um momento para o webhook processar (opcional mas recomendado)
  await new Promise(resolve => setTimeout(resolve, 2000));

  try {
    let subscription = null;

    // Tenta buscar por preapprovalId primeiro (mais confiável)
    if (preapprovalId) {
      try {
        console.log("🔎 Buscando assinatura por preapprovalId:", preapprovalId);
        const { data } = await api.get(`/payment/status-by-preapproval/${preapprovalId}`);
        subscription = data;
      } catch (err) {
        console.warn("⚠️ Não encontrou por preapprovalId, tentando por external_reference");
      }
    }

    // Fallback: busca por external_reference (subscriptionId)
    if (!subscription && externalReference) {
      try {
        console.log("🔎 Buscando assinatura por ID:", externalReference);
        const { data } = await api.get(`/payment/status/${externalReference}`);
        subscription = data;
      } catch (err) {
        console.error("❌ Erro ao buscar assinatura:", err);
      }
    }

    // Processa o resultado
    if (subscription) {
      const status = (subscription.status || '').toLowerCase();
      const paymentStatus = (subscription.paymentStatus || '').toLowerCase();

      console.log("📊 Status da assinatura:", { status, paymentStatus });

      if (status === 'active' || paymentStatus === 'approved') {

        result.value = {
          success: true,
          message: 'Assinatura ativada com sucesso! Bem-vindo ao Litterae. 🎉'
        };

        // Recarrega o perfil. Isso consome paymentConversionPending em
        // useUserStore._handlePaymentConversionPending — ponto único que dispara
        // o evento de conversão (Google/Meta) quando o backend marca a flag.
        await userStore.refreshCurrentUser();
      } else if (status === 'cancelled' || status === 'paused' || paymentStatus === 'cancelled') {
        result.value = {
          success: false,
          message: 'A autorização da assinatura foi cancelada. Por favor, tente novamente.'
        };
      } else {
        // Pendente
        result.value = {
          success: false,
          message: 'Sua assinatura está sendo processada. Você receberá uma confirmação em breve.'
        };
      }
    } else {
      // Não encontrou a assinatura
      console.warn("⚠️ Assinatura não encontrada");
      result.value = {
        success: false,
        message: 'Não foi possível verificar o status da assinatura. Por favor, verifique seu email ou contate o suporte.'
      };
    }
  } catch (err) {
    console.error("❌ Erro ao processar callback:", err);
    result.value = {
      success: false,
      message: 'Ocorreu um erro ao processar sua assinatura. Por favor, contate o suporte.'
    };
  } finally {
    isLoading.value = false;
  }
});

const handleComplete = () => {
  if (result.value.success) {
    // Se completou o onboarding, vai para o dashboard
    if (userStore.currentUser?.onboardingComplete) {
      router.push({ name: 'DashboardHome' });
    } else {
      // Se ainda não completou, vai para a próxima etapa do onboarding
      router.push({ name: 'UpgradePage' });
    }
  } else {
    // Se falhou, redireciona para tentar novamente
    router.push({ name: 'UpgradePage' });
  }
};

const handleRetry = () => {
  router.push({ name: 'UpgradePage' });
};
</script>

<template>
  <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--bg-main)] to-[#ffe6d5] p-4">
    <div class="max-w-lg w-full bg-surface rounded-2xl shadow-2xl overflow-hidden">
      <!-- Loading State -->
      <div v-if="isLoading" class="p-12 text-center">
        <div class="mx-auto flex h-20 w-20 items-center justify-center">
          <div class="animate-spin rounded-full h-16 w-16 border-b-4 border-[color:var(--text-important)]"></div>
        </div>
        <h3 class="mt-6 text-2xl font-semibold text-[color:var(--text-dark)]">Processando sua assinatura...</h3>
        <p class="mt-3 text-base text-[color:var(--text-light)]">
          Aguarde enquanto verificamos o status da sua autorização no Mercado Pago.
        </p>
      </div>

      <!-- Result State (usando seu componente existente como referência) -->
      <div v-else class="p-8">
        <div class="text-center flex flex-col items-center justify-center">
          <!-- Estado de Sucesso -->
          <div v-if="result.success">
            <div class="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <svg class="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 class="mt-6 text-3xl font-semibold text-[color:var(--text-dark)]">Assinatura Autorizada!</h3>
            <p class="mt-4 text-lg text-[color:var(--text-light)]">{{ result.message }}</p>
            <div class="mt-8 w-full">
              <button
                @click="handleComplete"
                type="button"
                class="w-full inline-flex items-center justify-center rounded-lg px-6 py-4 text-lg font-semibold bg-[var(--bg-sidebar-left)] text-[color:var(--text-dark)] transition hover:opacity-90 shadow-lg hover:shadow-xl"
              >
                Comece sua jornada já! 🚀
              </button>
            </div>
          </div>

          <!-- Estado de Falha -->
          <div v-else>
            <div class="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
              <svg class="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 class="mt-6 text-3xl font-semibold text-[color:var(--text-dark)]">Ocorreu um Problema</h3>
            <p class="mt-4 text-lg text-[color:var(--text-light)]">{{ result.message }}</p>
            <div class="mt-8 w-full space-y-3">
              <button
                @click="handleRetry"
                type="button"
                class="w-full inline-flex items-center justify-center rounded-lg px-6 py-4 text-lg font-semibold text-white transition bg-[var(--text-important)] hover:bg-opacity-90 shadow-lg hover:shadow-xl"
              >
                Tentar Novamente
              </button>
              <button
                @click="handleComplete"
                type="button"
                class="w-full inline-flex items-center justify-center rounded-lg px-6 py-4 text-lg font-semibold bg-surface text-[color:var(--text-dark)] border-2 border-[color:var(--border-color)] transition hover:bg-[#f8f9fa]"
              >
                Ir para o Painel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>